/**
 * Story Core System
 * Provides IntersectionObserver, utilities, and event system for story modules
 * Note: Uses IIFE pattern for Ghost's concatenated build system
 */

(function (window) {
  'use strict';

  class StoryCore {
    constructor(rootSelector) {
      this.root = document.querySelector(rootSelector || '[data-story-template]');

      if (!this.root) {
        console.warn('StoryCore: Root element not found');
        return;
      }

      this.templateType = this.root.dataset.storyTemplate;
      this.hasReducedMotion = this.checkReducedMotion();
      this.modules = new Map();
      this.observers = new Map();

      // Central IntersectionObserver instances
      this.setupObservers();
    }

    /**
     * Check for reduced motion preference
     */
    checkReducedMotion() {
      return !!(
        window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
      );
    }

    /**
     * Setup default IntersectionObserver instances
     */
    setupObservers() {
      // Standard reveal observer (10% from bottom)
      this.observers.set('reveal', new IntersectionObserver(
        (entries) => this.handleIntersection(entries, 'reveal'),
        {
          rootMargin: '0px 0px -10% 0px',
          threshold: 0.1
        }
      ));

      // Video/media observer (50% visible for better single-video behavior)
      this.observers.set('media', new IntersectionObserver(
        (entries) => this.handleIntersection(entries, 'media'),
        {
          rootMargin: '0px',
          threshold: 0.5
        }
      ));

      // Lazy load observer (just entered viewport)
      this.observers.set('lazy', new IntersectionObserver(
        (entries) => this.handleIntersection(entries, 'lazy'),
        {
          rootMargin: '50px',
          threshold: 0
        }
      ));
    }

    /**
     * Handle intersection events and dispatch to modules
     */
    handleIntersection(entries, observerType) {
      for (const entry of entries) {
        const eventName = entry.isIntersecting ? 'enter' : 'exit';
        const customEvent = new CustomEvent(`story:${observerType}:${eventName}`, {
          detail: { entry, observerType }
        });
        entry.target.dispatchEvent(customEvent);
      }
    }

    /**
     * Register a module with the core system
     */
    registerModule(name, moduleInstance) {
      this.modules.set(name, moduleInstance);
    }

    /**
     * Observe elements with a specific observer
     */
    observe(elements, observerType) {
      observerType = observerType || 'reveal';
      const observer = this.observers.get(observerType);
      if (!observer) {
        console.warn('StoryCore: Observer "' + observerType + '" not found');
        return;
      }

      const elementArray = Array.isArray(elements)
        ? elements
        : Array.from(elements);

      elementArray.forEach(function(el) { observer.observe(el); });
    }

    /**
     * Unobserve elements
     */
    unobserve(elements, observerType) {
      observerType = observerType || 'reveal';
      const observer = this.observers.get(observerType);
      if (!observer) return;

      const elementArray = Array.isArray(elements)
        ? elements
        : Array.from(elements);

      elementArray.forEach(function(el) { observer.unobserve(el); });
    }

    /**
     * Utility: Clamp value between min and max
     */
    clamp(value, min, max) {
      return Math.max(min, Math.min(max, value));
    }

    /**
     * Utility: Request Animation Frame wrapper
     */
    raf(callback) {
      if (this.hasReducedMotion) {
        callback();
        return null;
      }
      return requestAnimationFrame(callback);
    }

    /**
     * Destroy all observers and cleanup
     */
    destroy() {
      this.observers.forEach(function(observer) { observer.disconnect(); });
      this.observers.clear();
      this.modules.clear();
    }
  }

  // Expose StoryCore to window for other modules
  window.StoryCore = StoryCore;

})(window);
